import type React from 'react';
import {
  forwardRef,
  type CSSProperties,
  type FocusEventHandler,
  type HTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
} from 'react';
import isPropValid from '@emotion/is-prop-valid';
import { InfoIcon } from '@phosphor-icons/react';
import { cn, Tooltip } from '@skupreme/shared/ui';
import { toPlacement } from '@utility/popup-placement';
import type {
  FieldWidth,
  FormHelpToolTipProps,
} from 'types/common/form-fields';
import {
  FIELD_FRAME,
  FIELD_LABEL,
  FIELD_MESSAGE,
  fieldMargin,
  widthClass,
} from './styles';

/*
 * Callers spread Semantic-only props (`fluid`, `labeled`…) and their own
 * (`maxValue`…) through these fields. The old styled wrappers dropped the ones
 * the DOM does not know; this keeps that, so they do not land as attributes.
 */
export function domProps<T extends object>(props: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(props).filter(([key]) => isPropValid(key)),
  ) as Partial<T>;
}

type DivProps = HTMLAttributes<HTMLDivElement>;

export const FormInputSuffix = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div
      {...rest}
      ref={ref}
      className={cn(
        'sk:pointer-events-none sk:absolute sk:right-3 sk:text-xs sk:leading-4 sk:text-fg-tertiary',
        className,
      )}
    />
  ),
);
FormInputSuffix.displayName = 'FormInputSuffix';

export const FormTitle = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div
      {...rest}
      ref={ref}
      className={cn(
        'sk:text-xl sk:leading-7 sk:font-semibold sk:text-fg',
        className,
      )}
    />
  ),
);
FormTitle.displayName = 'FormTitle';

export const FormDescription = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div
      {...rest}
      ref={ref}
      className={cn('sk:text-dense sk:text-fg-tertiary', className)}
    />
  ),
);
FormDescription.displayName = 'FormDescription';

type LabelProps = LabelHTMLAttributes<HTMLLabelElement>;

/**
 * The quiet label above a field. `has-description` makes it the bolder
 * heading used over a description line.
 */
export const MuteLabel = forwardRef<HTMLLabelElement, LabelProps>(
  ({ className, ...rest }, ref) => (
    // biome-ignore lint/a11y/noLabelWithoutControl: callers pass htmlFor or wrap the control
    <label {...rest} ref={ref} className={cn(FIELD_LABEL, className)} />
  ),
);
MuteLabel.displayName = 'MuteLabel';

/** A label with the required mark after it. */
export const FieldRequiredLabel = forwardRef<HTMLLabelElement, LabelProps>(
  ({ className, children, ...rest }, ref) => (
    // biome-ignore lint/a11y/noLabelWithoutControl: callers pass htmlFor or wrap the control
    <label
      {...rest}
      ref={ref}
      className={cn(FIELD_LABEL, 'sk:flex!', className)}
    >
      {children}
      <RequiredMark />
    </label>
  ),
);
FieldRequiredLabel.displayName = 'FieldRequiredLabel';

export function RequiredMark() {
  return (
    <span aria-hidden className="sk:ml-0.5 sk:text-danger">
      *
    </span>
  );
}

export const FormHelpLabel = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div
      {...rest}
      ref={ref}
      className={cn(
        'sk:mb-1 sk:flex sk:items-center sk:[&>label]:mb-0!',
        className,
      )}
    />
  ),
);
FormHelpLabel.displayName = 'FormHelpLabel';

/** The validation message under a field. */
export function FieldMessage({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span role="alert" className={cn(FIELD_MESSAGE, className)}>
      {children}
    </span>
  );
}

type FieldFrameProps = Omit<DivProps, 'onBlur'> & {
  width?: FieldWidth;
  inline?: boolean;
  /* Accepted for Semantic's `Form.Field` API; the label and control show them. */
  required?: boolean;
  error?: boolean;
  disabled?: boolean;
  onBlur?: FocusEventHandler<HTMLDivElement> | (() => void);
  style?: CSSProperties;
};

/** The frame of one field: label, control and message, stacked. */
export const CustomField = forwardRef<HTMLDivElement, FieldFrameProps>(
  (
    {
      width,
      inline,
      required: _required,
      error: _error,
      disabled: _disabled,
      className,
      children,
      ...rest
    },
    ref,
  ) => (
    <div
      {...domProps(rest)}
      ref={ref}
      className={cn(
        FIELD_FRAME,
        widthClass(width),
        inline && 'sk:flex sk:items-center sk:gap-2',
        className,
        fieldMargin(className),
      )}
    >
      {children}
    </div>
  ),
);
CustomField.displayName = 'CustomField';

type InputContainerProps = DivProps & { inline?: boolean };

/** Positions an input with whatever sits at its edges (suffixes, counters). */
export function InputContainer({
  inline,
  className,
  ...rest
}: InputContainerProps) {
  return (
    <div
      {...rest}
      className={cn(
        inline ? 'sk:inline-flex' : 'sk:flex',
        'sk:relative sk:items-center',
        'sk:[&.align-unset]:items-[unset]',
        className,
      )}
    />
  );
}

/** Label, required mark and help tooltip, as most fields put them. */
export function FieldLabel({
  label,
  required,
  helpText,
  helpPosition,
  htmlFor,
  id,
  className,
}: {
  label?: ReactNode;
  required?: boolean;
  helpText?: ReactNode;
  helpPosition?: FormHelpToolTipProps['helpPosition'];
  htmlFor?: string;
  id?: string;
  className?: string;
}) {
  if (!label) {
    return null;
  }
  return (
    <MuteLabel
      id={id}
      htmlFor={htmlFor}
      className={cn('sk:mb-1 sk:flex! sk:items-center', className)}
    >
      {label}
      {required ? <RequiredMark /> : null}
      <FormHelpToolTip helpText={helpText} helpPosition={helpPosition} />
    </MuteLabel>
  );
}

/** An info mark that explains a field on hover or keyboard focus. */
export function FormHelpToolTip({
  helpText = '',
  helpPosition = 'top center',
  leftMargin = true,
  rightMargin = false,
  triggerClassName = '',
  className,
  contentWidth,
  contentMaxHight,
  padding,
  setOpen,
  disabled,
  open,
  style,
}: FormHelpToolTipProps) {
  if (!helpText) {
    return null;
  }

  const fixedWidth = !!contentWidth && contentWidth > 0;

  return (
    <Tooltip
      placement={toPlacement(helpPosition)}
      onOpenChange={setOpen}
      isDisabled={disabled}
      isOpen={open}
      className={cn(fixedWidth && 'sk:max-w-none', className)}
      triggerClassName={cn(
        'sk:cursor-help sk:text-fg-tertiary sk:align-middle',
        leftMargin && 'sk:ml-1.5',
        rightMargin && 'sk:mr-1.5',
        triggerClassName,
      )}
      content={
        <div
          className="sk:overflow-y-auto"
          style={{
            padding,
            maxHeight: contentMaxHight,
            ...(fixedWidth
              ? { minWidth: contentWidth, maxWidth: contentWidth }
              : {}),
            ...style,
          }}
        >
          {helpText}
        </div>
      }
    >
      <InfoIcon size={14} aria-label="More information" />
    </Tooltip>
  );
}

export const InputRight = forwardRef<HTMLSpanElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <span
      {...rest}
      ref={ref}
      className={cn(
        'sk:pointer-events-none sk:absolute sk:right-3 sk:bg-surface sk:pl-2 sk:text-xs sk:text-fg-tertiary',
        className,
      )}
    />
  ),
);
InputRight.displayName = 'InputRight';

export function SimpleInputRight(props: { children?: React.ReactNode }) {
  const { children } = props;
  return <InputRight>{children}</InputRight>;
}
